import React from 'react';
import styled, { css } from 'styled-components';

// Icons
import IconTrash from '../../../../../icons/trash';
import IconPlus from '../../../../../icons/plus';

// Context
import { useConfigDragAndDropTableContext } from '../../../context/config-drag-and-drop-table-context';

const StyledButton = css<{ $disabled: boolean }>`
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-weight: 500;
    font-size: 14px;
    background-color: ${props => (props.$disabled ? props.theme.colours.midGrey : props.theme.colours.offBlackLighter)};
    color: ${props => props.theme.colours.white};
    text-transform: uppercase;
    border-radius: 2px;
    border: 0;
    padding: 10px 15px;
    cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
`;

const StyledPrimaryButton = styled.button<{ $disabled: boolean }>`
    ${StyledButton}
`;

const StyledDeleteButton = styled.button<{ $disabled: boolean }>`
    ${StyledButton}
    background-color: ${props => (props.$disabled ? props.theme.colours.midGrey : props.theme.colours.red)};
`;

const StyledAddButton = styled.button<{ $disabled: boolean }>`
    ${StyledButton}
    background-color: ${props => (props.$disabled ? props.theme.colours.midGrey : props.theme.colours.green)};
`;

const StyledIconTrash = styled(IconTrash)`
    height: 15px;
`;

const StyledIconPlus = styled(IconPlus)`
    height: 15px;
`;

type ActionButtonProps = {
    type: 'primary' | 'delete' | 'add' | 'save' | 'cancel';
    label: string;
    isDisabled?: boolean;
    onClick: () => void;
};

const ActionButton = ({ type, label, isDisabled, onClick }: ActionButtonProps) => {
    const { status, tableDisabled } = useConfigDragAndDropTableContext();

    const handleClick = () => {
        if (!tableDisabled && status === 'success') {
            onClick();
        }
    };

    if (status) {
        switch (type) {
            case 'primary':
                return (
                    <StyledPrimaryButton
                        $disabled={isDisabled || tableDisabled || status !== 'success'}
                        onClick={handleClick}
                    >
                        {label}
                    </StyledPrimaryButton>
                );
            case 'delete':
                return (
                    <StyledDeleteButton
                        $disabled={isDisabled || tableDisabled || status !== 'success'}
                        onClick={handleClick}
                    >
                        <StyledIconTrash />
                        {label}
                    </StyledDeleteButton>
                );
            case 'add':
                return (
                    <StyledAddButton
                        $disabled={isDisabled || tableDisabled || status !== 'success'}
                        onClick={handleClick}
                    >
                        <StyledIconPlus />
                        {label}
                    </StyledAddButton>
                );
            case 'save':
                return (
                    <StyledAddButton
                        $disabled={isDisabled || tableDisabled || status !== 'success'}
                        onClick={handleClick}
                    >
                        {label}
                    </StyledAddButton>
                );
            case 'cancel':
                return (
                    <StyledDeleteButton
                        $disabled={isDisabled || tableDisabled || status !== 'success'}
                        onClick={handleClick}
                    >
                        {label}
                    </StyledDeleteButton>
                );
            default:
                return null;
        }
    }
    return null;
};

export default ActionButton;
